/**
 * AdminViewBar — orange "Admin mode" indicator bar.
 *
 * Renders at the top of `<AdminShell>` whenever the user is on an
 * admin view AND not impersonating. Mutually exclusive with the red
 * `<AdminModeBar>` (impersonation bar) by construction:
 *
 *   - plain admin viewing /admin/*  → orange bar (this component)
 *   - admin impersonating someone    → red bar (`AdminModeBar.jsx`)
 *   - never both                     → guarded by the `!is_impersonating`
 *                                       check here + the `is_impersonating`
 *                                       check on the red bar
 *
 * Style: full-width, brand orange #F89623, slate text #2B3942, lucide
 * Shield icon, label "Admin mode — {admin email}". NORMAL FLOW (not
 * `fixed`) so it takes its own ~36px in the AdminShell vertical flex
 * column — no global `pt-9` padding hack needed. The red bar uses
 * `fixed` because it lives at the AppShell root above all routes; the
 * orange bar lives INSIDE AdminShell, so normal flow is correct.
 *
 * Reverses Phase 1 D13 (which hid the red bar for plain admin under
 * "slate sidebar is itself the admin indicator"). User has clarified
 * that two distinct visual states deserve two distinct bars: orange
 * for "you're in admin", red for "you're acting as someone else".
 */
import React from "react";
import { Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function AdminViewBar() {
  const { user, view } = useAuth();

  // Mutually exclusive with the red impersonation bar. view === "admin"
  // is only true on /admin/* routes (URL→view sync, RouteViewSync.jsx);
  // !is_impersonating excludes the impersonation case (Backlog D will
  // set this flag — until then it's always falsy, which is fine).
  if (view !== "admin") return null;
  if (user?.is_impersonating) return null;
  if (!user?.email) return null;

  return (
    <div
      className="w-full px-6 py-2 flex items-center gap-2 text-sm font-semibold"
      style={{ backgroundColor: "#F89623", color: "#2B3942" }}
      data-testid="admin-view-bar"
      role="status"
      aria-label="Admin mode active"
    >
      <Shield size={16} strokeWidth={2.5} aria-hidden="true" />
      <span data-testid="admin-view-bar-email">
        Admin mode — {user.email}
      </span>
    </div>
  );
}
